import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Avatar, Button, Icon, IconButton, RoleChip } from '@w7/ui'
import { navForLevel, type NavGroup, type NavItem } from './nav'
import { ROLE_LABEL } from '@/data/types'
import { isDemoSession, logout, claims, clearAllAuth } from '@/auth/oidc'
import { clearLabSession, getLabAdminLevel, getLabUsername } from '@/config/lab'
import { bookmakerApi } from '@/api/bookmaker'
import { StrixMark } from '@/features/auth/StrixMark'
import './bm-shell.css'

const RAIL_COLLAPSED_KEY = 'w7.rail.collapsed'

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(RAIL_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function pathMatchesItem(pathname: string, item: NavItem): boolean {
  const match = item.match ?? item.to
  if (match === '/') return pathname === '/'
  return pathname === match || pathname.startsWith(`${match}/`)
}

function groupHasActive(pathname: string, group: NavGroup): boolean {
  return group.items.some((item) => pathMatchesItem(pathname, item))
}

/**
 * The authenticated shell.
 *
 * Rail is viewport-locked (does not scroll with page content). The nav list
 * scrolls only when items overflow — typical on short viewports. Desktop can
 * collapse to icon-only with tooltips; preference persists in localStorage.
 */

export function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const [railOpen, setRailOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const navGroups = useMemo(() => navForLevel(getLabAdminLevel() || 'Admin'), [])
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setCollapsed(readCollapsedPreference())
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      /* private mode / blocked storage — preference is session-only */
    }
  }, [collapsed])

  // Any navigation closes the mobile rail — leaving it open over the new page
  // is the single most common phone-nav bug.
  useEffect(() => setRailOpen(false), [location.pathname])

  // Lab Create / List: keep accordion open when a child route is active.
  useEffect(() => {
    setOpenAccordions((prev) => {
      const next = { ...prev }
      for (const group of navGroups) {
        if (!group.accordion) continue
        if (groupHasActive(location.pathname, group)) next[group.id] = true
      }
      return next
    })
  }, [location.pathname, navGroups])

  const toggleCollapsed = () => setCollapsed((value) => !value)

  function toggleAccordion(id: string) {
    setOpenAccordions((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <div className="w7-app bm-app">
      <a href="#main" className="w7-skip">
        Skip to content
      </a>

      <aside
        className="w7-rail"
        data-open={railOpen ? 'true' : undefined}
        data-collapsed={collapsed ? 'true' : undefined}
        aria-label="Console navigation"
      >
        <div className="w7-rail-head">
          <Link to="/InPlay" className="strix-mark-link" aria-label="Book Maker — In Play">
            <StrixMark
              size={collapsed ? 28 : 26}
              showName={!collapsed}
              suffix={collapsed ? undefined : 'Book Maker'}
            />
          </Link>
          <IconButton
            name="menu"
            label={collapsed ? 'Expand menu' : 'Collapse menu'}
            className="w7-rail-collapse"
            onClick={toggleCollapsed}
          />
          <IconButton
            name="close"
            label="Close menu"
            className="w7-rail-close"
            onClick={() => setRailOpen(false)}
          />
        </div>

        <nav className="w7-rail-scroll bm-rail-nav" aria-label="Console sections">
          {navGroups.map((group) => {
            if (group.accordion && group.label) {
              const open = openAccordions[group.id] ?? false
              return (
                <div key={group.id} className="bm-nav-accordion" data-open={open ? 'true' : undefined}>
                  <button
                    type="button"
                    className="bm-nav-accordion-head"
                    aria-expanded={open}
                    onClick={() => toggleAccordion(group.id)}
                    title={collapsed ? group.label : undefined}
                  >
                    <span className="bm-nav-accordion-label">{group.label}</span>
                    <Icon name={open ? 'chevron-up' : 'chevron-down'} size={14} />
                  </button>
                  {open || collapsed ? (
                    <div className="bm-nav-accordion-body">
                      {group.items.map((item) => (
                        <NavItemLink key={item.id} item={item} collapsed={collapsed} nested />
                      ))}
                    </div>
                  ) : null}
                </div>
              )
            }

            return (
              <div key={group.id} className="w7-navgroup bm-nav-flat">
                {group.items.map((item) => (
                  <NavItemLink key={item.id} item={item} collapsed={collapsed} />
                ))}
              </div>
            )
          })}
        </nav>
      </aside>

      {/* Its OWN scrim class, not the modal one. The shared `w7-scrim` sits at
          z-index 100 so it can cover a drawer at 101; the mobile rail is at 60,
          so reusing it put the scrim ON TOP of the open menu and swallowed every
          tap — the menu opened and then did nothing. */}
      {railOpen ? (
        <div className="w7-scrim w7-scrim-nav" onClick={() => setRailOpen(false)} />
      ) : null}

      <div className="w7-main">
        <Topbar onOpenRail={() => setRailOpen(true)} />
        <main className="w7-content" id="main">
          {children}
        </main>
      </div>
    </div>
  )
}

function NavItemLink({
  item,
  collapsed,
  nested = false,
}: {
  item: NavItem
  collapsed: boolean
  nested?: boolean
}) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={nested ? 'w7-navitem bm-nav-nested' : 'w7-navitem'}
      title={collapsed ? `${item.label} — ${item.description}` : item.description}
      aria-label={collapsed ? item.label : undefined}
    >
      <span className="w7-navitem-icon" aria-hidden="true">
        <Icon name={item.icon} size={18} />
      </span>
      <span className="w7-navitem-label">{item.label}</span>
    </NavLink>
  )
}

function Topbar({ onOpenRail }: { onOpenRail: () => void }) {
  const demo = isDemoSession()
  const who = claims()
  const labUser = getLabUsername()
  const signedInAs =
    labUser ||
    who?.preferredUsername ||
    who?.email ||
    (demo ? 'demo' : 'Signed in')

  async function onLogout() {
    try {
      await bookmakerApi.userLogout({})
    } catch {
      /* ignore — still clear local session */
    }
    clearLabSession()
    clearAllAuth()
    logout({ silent: true })
  }

  return (
    <header className="w7-topbar">
      <IconButton name="menu" label="Open menu" className="w7-rail-toggle" onClick={onOpenRail} />

      <span className="w7-spacer" />

      <div className="w7-topbar-trail bm-topbar-user">
        <span className="bm-topbar-username" title={signedInAs}>
          {signedInAs}
        </span>
        <Button size="sm" variant="secondary" icon="logout" onClick={() => void onLogout()}>
          Logout
        </Button>
      </div>
    </header>
  )
}

/** Identity block reused by the downline table and the member header. */
export function AccountCell({
  username,
  displayName,
  role,
  to,
  meta,
}: {
  username: string
  displayName: string
  role: string
  to?: string
  meta?: React.ReactNode
}) {
  const body = (
    <>
      <Avatar name={displayName || username} size="sm" />
      <span style={{ minWidth: 0 }}>
        <span className="w7-row" style={{ gap: 6 }}>
          <span className="w7-truncate" style={{ fontWeight: 600 }}>
            {username}
          </span>
          <RoleChip role={role} label={ROLE_LABEL[role as keyof typeof ROLE_LABEL] ?? role} />
        </span>
        <span className="w7-dim w7-truncate" style={{ display: 'block', fontSize: 11.5 }}>
          {meta ?? displayName}
        </span>
      </span>
    </>
  )

  return to ? (
    <Link to={to} className="w7-identity">
      {body}
    </Link>
  ) : (
    <span className="w7-identity">{body}</span>
  )
}

export { Button }
